import i18n from "i18next";
import type { ErrorCode, ServerErrorMessage } from "@binogi-livequiz/shared";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { ar } from "../locales/ar";
import { de } from "../locales/de";
import { en } from "../locales/en";
import { es } from "../locales/es";
import { fi } from "../locales/fi";
import { fr } from "../locales/fr";
import { hi } from "../locales/hi";
import { pl } from "../locales/pl";
import { prs } from "../locales/prs";
import { ru } from "../locales/ru";
import { so } from "../locales/so";
import { sv } from "../locales/sv";
import { th } from "../locales/th";
import { ti } from "../locales/ti";
import { tr } from "../locales/tr";
import { uk } from "../locales/uk";

// The set of languages the Binogi lesson catalog actually translates
// content into (see stream.binogi.net/stream/files.json — cut-off: at
// least ~500 lessons in that language). All 16 have full UI-chrome
// translations; English is the source of truth. Fallback is English
// so a missing key never shows the raw key — it shows the English copy.
export const SUPPORTED_LANGUAGES = [
  { code: "ar",  native: "العربية",     english: "Arabic" },
  { code: "de",  native: "Deutsch",     english: "German" },
  { code: "en",  native: "English",     english: "English" },
  { code: "es",  native: "Español",     english: "Spanish" },
  { code: "fi",  native: "Suomi",       english: "Finnish" },
  { code: "fr",  native: "Français",    english: "French" },
  { code: "hi",  native: "हिन्दी",       english: "Hindi" },
  { code: "pl",  native: "Polski",      english: "Polish" },
  { code: "prs", native: "دری",         english: "Dari" },
  { code: "ru",  native: "Русский",     english: "Russian" },
  { code: "so",  native: "Soomaali",    english: "Somali" },
  { code: "sv",  native: "Svenska",     english: "Swedish" },
  { code: "th",  native: "ไทย",         english: "Thai" },
  { code: "ti",  native: "ትግርኛ",       english: "Tigrinya" },
  { code: "tr",  native: "Türkçe",      english: "Turkish" },
  { code: "uk",  native: "Українська",  english: "Ukrainian" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar:  { translation: ar  },
      de:  { translation: de  },
      en:  { translation: en  },
      es:  { translation: es  },
      fi:  { translation: fi  },
      fr:  { translation: fr  },
      hi:  { translation: hi  },
      pl:  { translation: pl  },
      prs: { translation: prs },
      ru:  { translation: ru  },
      so:  { translation: so  },
      sv:  { translation: sv  },
      th:  { translation: th  },
      ti:  { translation: ti  },
      tr:  { translation: tr  },
      uk:  { translation: uk  },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // match en-US → en, sv-SE → sv
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "binogi-buzz:lng",
      caches: ["localStorage"],
    },
  });

// The i18n key a screen shows for each server ERROR code. The server's own
// `message` is English and meant for logs, so it is never shown. A code
// with nothing a teacher or student can act on uses the screen's fallback.
// One key covers every server message sent under its code: SESSION_NOT_FOUND
// is also "not attached", SESSION_NOT_ACTIVE also "answer arrived too late".
export const SERVER_ERROR_KEYS: Record<ErrorCode, string | null> = {
  UNKNOWN_MESSAGE_TYPE: null,
  STALE_CLIENT: "errors.stale_client",
  INVALID_PAYLOAD: null,
  SESSION_NOT_FOUND: "errors.session_not_found",
  SESSION_NOT_OPEN: "errors.session_not_open",
  SESSION_NOT_ACTIVE: "errors.session_not_active",
  NOT_TEACHER: "errors.not_teacher",
  BAD_HOST_TOKEN: null,
  ALREADY_ANSWERED: "errors.already_answered",
  WRONG_QUESTION_INDEX: "errors.wrong_question_index",
  NAME_REQUIRED: "join.error_name_required",
  NAME_TAKEN: "errors.name_taken",
  INTERNAL: null,
};

/**
 * The i18n key for a server ERROR frame. `overrides` gives a screen its own
 * wording for some codes; `fallback` covers a code with no useful copy or
 * one this bundle does not know.
 */
export function serverErrorKey(
  msg: ServerErrorMessage,
  {
    overrides = {},
    fallback = "errors.generic",
  }: { overrides?: Partial<Record<ErrorCode, string>>; fallback?: string } = {},
): string {
  // Typed as an ErrorCode, but a bundle older than the server can still
  // receive a code it does not know: the guard keeps that one readable.
  const code = msg.code;
  if (!Object.hasOwn(SERVER_ERROR_KEYS, code)) return fallback;
  return overrides[code] ?? SERVER_ERROR_KEYS[code] ?? fallback;
}

export default i18n;
